import { useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { Loader2 } from "lucide-react";
import { Plugs } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  PageActions,
  PageBreadcrumb,
} from "@/components/atomic-crm/layout/PageActions";
import { cn } from "@/lib/utils";
import {
  INTEGRATION_LABELS,
  MODULE_IDS,
  MODULES,
  type ModuleId,
} from "@/modules/registry";
import { MODULE_CATALOG } from "@/modules/apps/moduleCatalog";
import {
  enabledDependents,
  isOn,
  missingDependencies,
} from "@/modules/apps/moduleDependencies";
import { ModuleAutomationSwitches } from "@/modules/apps/ModuleAutomationSwitches";
import { ModuleFeatureSwitches } from "@/modules/apps/ModuleFeatureSwitches";
import { ModuleSetupChecklist } from "@/modules/apps/ModuleSetupChecklist";
import { ModuleSetupDialog } from "@/modules/apps/ModuleSetupDialog";
import { useModuleSetup } from "@/modules/apps/useModuleSetup";
import { useOrgModuleRows } from "@/modules/apps/useOrgModuleRows";

const isModuleId = (value: string | undefined): value is ModuleId =>
  MODULE_IDS.includes(value as ModuleId);

/** Store page of one module: what it does, how it fits, turn it on. */
export const ModuleLandingPage = () => {
  const { moduleId } = useParams();
  if (!isModuleId(moduleId)) return <Navigate to="/apps" replace />;
  return <ModuleLanding moduleId={moduleId} />;
};

const ModuleLanding = ({ moduleId }: { moduleId: ModuleId }) => {
  const module = MODULES[moduleId];
  const content = MODULE_CATALOG[moduleId];
  const { rows, isAdmin, busyModuleId, apply } = useOrgModuleRows();
  const on = isOn(rows, moduleId);
  const setup = useModuleSetup(on ? moduleId : null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const busy = busyModuleId != null;
  const dependents = enabledDependents(rows, moduleId);
  const missing = missingDependencies(rows, moduleId);
  const row = rows.find((r) => r.module_id === moduleId);
  const Icon = module.icon;

  const turnOn = async () => {
    const saved = await apply([
      ...missing.map((id) => ({ moduleId: id, enabled: true })),
      { moduleId, enabled: true },
    ]);
    if (saved && content.setup.length > 0) setSetupOpen(true);
  };

  const cta = module.core ? (
    <Badge variant="secondary">Included in every workspace</Badge>
  ) : !isAdmin ? (
    <p className="text-sm text-muted-foreground">
      {on
        ? "Your workspace has this app."
        : "Ask your workspace administrator to add this app."}
    </p>
  ) : on ? (
    <div className="flex flex-wrap items-center gap-2">
      {content.setup.length > 0 ? (
        <Button type="button" onClick={() => setSetupOpen(true)}>
          {setup.isReady ? "Setup guide" : "Finish setup"}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        disabled={busy || dependents.length > 0}
        title={
          dependents.length
            ? `Needed by ${dependents.map((m) => m.name).join(", ")}`
            : undefined
        }
        onClick={() => void apply([{ moduleId, enabled: false }])}
      >
        {busy ? <Loader2 className="size-4 animate-spin" /> : null}
        Turn off
      </Button>
    </div>
  ) : (
    <Button
      type="button"
      size="lg"
      disabled={busy}
      onClick={() => void turnOn()}
    >
      {busy ? <Loader2 className="size-4 animate-spin" /> : null}
      Add {module.name}
    </Button>
  );

  return (
    <div className="mx-auto flex w-full max-w-5xl min-w-0 flex-col gap-8 pb-10">
      <PageActions>
        <PageBreadcrumb
          items={[{ label: "Apps", to: "/apps" }, { label: module.name }]}
        />
      </PageActions>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <span
          className={cn(
            "flex size-16 shrink-0 items-center justify-center rounded-2xl",
            on ? "bg-primary/10 text-primary" : "bg-muted text-foreground",
          )}
        >
          <Icon className="size-9" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold tracking-tight">
              {module.name}
            </h1>
            {on && !module.core ? <Badge variant="success">On</Badge> : null}
          </div>
          <p className="mt-1 text-base text-foreground">{content.tagline}</p>
          <p className="text-sm text-muted-foreground">{module.description}</p>
          {missing.length > 0 && !on ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Also adds {missing.map((id) => MODULES[id].name).join(", ")},
              which it needs.
            </p>
          ) : null}
        </div>
        <div className="shrink-0">{cta}</div>
      </header>

      {on && content.setup.length > 0 && !setup.isReady ? (
        <section className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4">
          <h2 className="text-sm font-semibold">
            Finish setting up {module.name} · {setup.requiredDone}/
            {setup.requiredTotal}
          </h2>
          <ModuleSetupChecklist
            steps={setup.steps}
            isPending={setup.isPending}
          />
        </section>
      ) : null}

      {on && module.options?.length && isAdmin ? (
        <section className="flex flex-col gap-2">
          {module.options.map((option) => (
            <label
              key={option.key}
              className="flex items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3"
            >
              <span>
                <span className="block text-sm font-medium">
                  {option.label}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {option.description}
                </span>
              </span>
              <Switch
                checked={row?.options?.[option.key] === true}
                disabled={busy}
                onCheckedChange={(checked) =>
                  void apply([
                    {
                      moduleId,
                      enabled: true,
                      options: {
                        ...(row?.options ?? {}),
                        [option.key]: checked,
                      },
                    },
                  ])
                }
              />
            </label>
          ))}
        </section>
      ) : null}

      {on && module.features?.length && isAdmin ? (
        <section className="flex flex-col gap-3">
          <div>
            <h2 className="text-sm font-semibold">Features</h2>
            <p className="text-xs text-muted-foreground">
              Extra tools inside {module.name}. Turn on only what your team
              uses.
            </p>
          </div>
          <ModuleFeatureSwitches
            module={module}
            rows={rows}
            busy={busy}
            onChange={(changes) => void apply(changes)}
          />
        </section>
      ) : null}

      {on && module.automations?.length && isAdmin ? (
        <section className="flex flex-col gap-3">
          <div>
            <h2 className="text-sm font-semibold">Automations</h2>
            <p className="text-xs text-muted-foreground">
              What {module.name} sends on its own. All of them are in Settings →
              Automations too.
            </p>
          </div>
          <ModuleAutomationSwitches
            module={module}
            rows={rows}
            busy={busy}
            onChange={(changes) => void apply(changes)}
          />
        </section>
      ) : null}

      <div className="overflow-hidden rounded-2xl border bg-muted/40">
        {imageFailed ? (
          <div className="flex aspect-[16/9] items-center justify-center text-muted-foreground">
            <Icon className="size-16 opacity-40" />
          </div>
        ) : (
          <img
            src={content.screenshot}
            alt={`${module.name} in Sigma`}
            className="block aspect-[16/9] w-full object-cover object-top"
            onError={() => setImageFailed(true)}
          />
        )}
      </div>

      <section className="grid gap-4 md:grid-cols-3">
        {content.benefits.map((benefit) => (
          <div key={benefit.title} className="rounded-xl border bg-card p-4">
            <p className="font-medium">{benefit.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {benefit.description}
            </p>
          </div>
        ))}
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        {content.worksWith.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold">Works with</h2>
            <ul className="flex flex-col gap-2">
              {content.worksWith.map(({ moduleId: otherId, how }) => {
                const other = MODULES[otherId];
                const OtherIcon = other.icon;
                return (
                  <li key={otherId}>
                    <Link
                      to={`/apps/${otherId}`}
                      className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 hover:border-primary/50"
                    >
                      <OtherIcon className="size-5 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">
                          {other.name}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {how}
                        </span>
                      </span>
                      {isOn(rows, otherId) ? (
                        <Badge variant="success">On</Badge>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold">What you need</h2>
          {module.requiresIntegrations.length === 0 &&
          content.setup.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing — it works as soon as it's on.
            </p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {module.requiresIntegrations.map((id) => (
                <li key={id} className="flex items-center gap-2">
                  <Plugs className="size-4 text-muted-foreground" />
                  {INTEGRATION_LABELS[id]}
                </li>
              ))}
              {content.setup
                .filter((step) => !step.optional)
                .map((step) => (
                  <li key={step.id} className="text-muted-foreground">
                    · {step.title}
                  </li>
                ))}
            </ul>
          )}
        </section>
      </div>

      <ModuleSetupDialog
        moduleId={moduleId}
        open={setupOpen}
        onOpenChange={setSetupOpen}
      />
    </div>
  );
};
